import React, { useEffect, useState } from "react";

// @mui/material components
import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Checkbox from "@mui/material/Checkbox";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";

// Soft UI Dashboard PRO React components
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import SoftButton from "components/SoftButton";

// Soft UI Dashboard PRO React example components
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import DataTable from "examples/Tables/DataTable";

// Data
import OrdersApi from "api/orders";
import { Button, CircularProgress, Pagination, Stack } from "@mui/material";
import ActionCell from "./components/ActionCell";
import FactureCell from "./components/FactureCell";
import { useTranslation } from "react-i18next";

function OnWayOrders() {
  const [menu, setMenu] = useState(null);
  const [orders, setOrders] = useState([]);
  const [selectedOrders, setSelectedOrders] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogType, setDialogType] = useState("");
  const [dialogTitle, setDialogTitle] = useState("");
  const [dialogContent, setDialogContent] = useState("");
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [anchor, setAnchor] = useState(null); // Anchor for menu
  const [nameSortOrder, setNameSortOrder] = useState("asc");
  const [dateSortOrder, setDateSortOrder] = useState("asc");

  const { t } = useTranslation();

  const openMenu = (event) => setMenu(event.currentTarget);
  const closeMenu = () => setMenu(null);

  const handleGetOrders = (page) => {
    const token = localStorage.getItem("token");

    if (!token) {
      console.error("No token found in localStorage.");
      setLoading(false);
      return;
    }

    OrdersApi.GetOnWayOrders(token, page)
      .then((response) => {
        if (response.data) {
          setOrders(response.data.results);
          setTotalPages(Math.ceil(response.data.count / 10));
        } else {
          console.warn("No data found:", response.data.msg);
        }
      })
      .catch((error) => {
        if (error.response) {
          console.error("Error response data:", error.response.data.msg);
          console.error("Full error response:", error.response);
        } else {
          console.error("There has been an error.");
        }
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    handleGetOrders(page);
  }, [page]);

  const handleSelectOrder = (id) => {
    setSelectedOrders((prevSelected) =>
      prevSelected?.includes(id)
        ? prevSelected.filter((orderId) => orderId !== id)
        : [...prevSelected, id]
    );
  };

  const handleOpenDialog = (type) => {
    setDialogType(type);
    if (type === "onway") {
      setDialogTitle(`${t("Confirmer la livraison")}`);
      setDialogContent(
        `${t("Êtes-vous sûr de vouloir livrer les commandes sélectionnées?")}`
      );
    } else if (type === "cancel") {
      setDialogTitle(`${t("Annuler les commandes")}`);
      setDialogContent(
        `${t("Êtes-vous sûr de vouloir annuler les commandes sélectionnées?")}`
      );
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
  };

  const handleConfirmAction = () => {
    const token = localStorage.getItem("token");
    if (!token) {
      console.error("No token found in localStorage.");
      setLoading(false);
      return;
    }

    const apiCalls = selectedOrders.map((orderId) => {
      if (dialogType === "onway") {
        return OrdersApi.DeliverOnWayOrders(token, orderId);
      } else if (dialogType === "cancel") {
        return OrdersApi.CancelOnWayOrders(token, orderId);
      }
      return null;
    });

    Promise.all(apiCalls)
      .then(() => {
        handleGetOrders();
        setSelectedOrders([]);
      })
      .catch((error) => {
        console.error("Error processing orders:", error);
      })
      .finally(() => {
        handleCloseDialog();
        setLoading(false);
      });
  };

  const handleDataUpdate = React.useCallback(() => {
    handleGetOrders(page);
    setSelectedOrders([]);
  }, [page]);

  const handlePageChange = (event, value) => {
    setPage(value);
  };

  const handleSortChange = (field) => {
    let newSortOrder;

    if (field === "username") {
      newSortOrder = nameSortOrder === "asc" ? "desc" : "asc";
      setNameSortOrder(newSortOrder);
      sortOrders("customer", newSortOrder);
    } else if (field === "created_at") {
      newSortOrder = dateSortOrder === "asc" ? "desc" : "asc";
      setDateSortOrder(newSortOrder);
      sortOrders("created_at", newSortOrder);
    }

    setAnchor(null); // Close the menu after sorting
  };

  const sortOrders = (field, order) => {
    const sortedOrders = [...orders].sort((a, b) => {
      const valueA =
        field === "created_at" ? new Date(a[field]) : a[field].toLowerCase();
      const valueB =
        field === "created_at" ? new Date(b[field]) : b[field].toLowerCase();

      if (order === "asc") {
        return valueA < valueB ? -1 : valueA > valueB ? 1 : 0;
      } else {
        return valueA > valueB ? -1 : valueA < valueB ? 1 : 0;
      }
    });

    setOrders(sortedOrders); // Update the orders with the sorted array
  };

  const columns = React.useMemo(
    () => [
      {
        Header: `${t("Client")}`,
        accessor: "customer",
        width: 70,
        Cell: ({ cell }) => (
          <div style={{ display: "flex", alignItems: "center" }}>
            <Checkbox
              checked={selectedOrders?.includes(cell.row.original.id)}
              onChange={() => handleSelectOrder(cell.row.original.id)}
            />
            <span style={{ marginLeft: 8 }}>{cell.row.original.customer}</span>
          </div>
        ),
      },
      // { Header: "Type de commande", accessor: "doc_type" },
      { Header: `${t("Prix total")}`, accessor: "total_price" },
      { Header: `${t("Méthode de paiement")}`, accessor: "payment_methode" },
      { Header: `${t("Bon de livraison")}`, accessor: "facture" },
      {
        Header: `${t("action")}`,
        accessor: "action",
        Cell: ({ row }) => (
          <ActionCell
            orderId={row.original.id}
            onDataUpdate={handleDataUpdate}
          />
        ),
      },
    ],
    [selectedOrders, handleDataUpdate, t]
  );

  const rows = React.useMemo(
    () =>
      orders.map((order) => ({
        id: order.id,
        customer: order.customer,
        // doc_type: order.doc_type,
        total_price: `${order.total_price} TND`,
        payment_methode: order.payment_methode,
        facture: <FactureCell delivery_note_id={order.delivery_note_id} />,
      })),
    [orders]
  );

  const renderMenu = (
    <Menu
      anchorEl={menu}
      anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      transformOrigin={{ vertical: "top", horizontal: "left" }}
      open={Boolean(menu)}
      onClose={closeMenu}
      keepMounted
    >
      <MenuItem
        onClick={() =>
          selectedOrders.length > 0 ? handleOpenDialog("onway") : null
        }
      >
        <SoftTypography variant="button" fontWeight="regular">
          {t("Livrer les commandes sélectionnées")}
        </SoftTypography>
      </MenuItem>
      <MenuItem
        onClick={() =>
          selectedOrders.length > 0 ? handleOpenDialog("cancel") : null
        }
      >
        <SoftTypography variant="button" color="error" fontWeight="regular">
          {t("Annuler les commandes sélectionnées")}
        </SoftTypography>
      </MenuItem>
    </Menu>
  );

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <SoftBox
        display="flex"
        flexDirection="column"
        justifyContent="space-between"
        minHeight="85vh"
      >
        <SoftBox my={3}>
          <SoftBox
            display="flex"
            justifyContent="space-between"
            alignItems="flex-start"
            mb={2}
          >
            <SoftButton
              variant={menu ? "contained" : "outlined"}
              color="dark"
              onClick={openMenu}
              disabled={orders.length === 0}
            >
              {t("État de la commande")}&nbsp;
              <Icon>keyboard_arrow_down</Icon>
            </SoftButton>
            {renderMenu}
            <SoftButton
              variant="outlined"
              color="dark"
              disabled={orders.length === 0}
              onClick={(e) => setAnchor(e.currentTarget)}
            >
              {t("Trier par nom ou date")} (⬆⬇)
            </SoftButton>
            <Menu
              anchorEl={anchor}
              open={Boolean(anchor)}
              onClose={() => setAnchor(null)}
            >
              <MenuItem onClick={() => handleSortChange("username")}>
                {t("Trier par Nom")} ({nameSortOrder === "asc" ? "⬆" : "⬇"})
              </MenuItem>
              <MenuItem onClick={() => handleSortChange("created_at")}>
                {t("Trier par Date")} ({dateSortOrder === "asc" ? "⬆" : "⬇"})
              </MenuItem>
            </Menu>

            {/* <SoftBox ml={1}>
            <SoftButton variant="outlined" color="dark">
              <Icon>description</Icon>
              &nbsp;Exporter CSV
            </SoftButton>
          </SoftBox> */}
          </SoftBox>
          <Card>
            {loading ? (
              <SoftBox
                display="flex"
                justifyContent="center"
                alignItems="center"
                p={35}
              >
                <CircularProgress sx={{ color: "#1F51FF" }} />
              </SoftBox>
            ) : (
              <>
                <DataTable
                  table={{ columns, rows }}
                  entriesPerPage={false}
                  canSearch={orders.length > 0}
                  columnMinWidth={70}
                />
                {totalPages > 1 && (
                  <Stack
                    spacing={2}
                    sx={{
                      mt: 2,
                      position: "absolute",
                      bottom: 7,
                      left: 100,
                      transform: "translateX(-50%)",
                    }}
                  >
                    <Pagination
                      count={totalPages}
                      page={page}
                      onChange={handlePageChange}
                      color="secondary"
                    />
                  </Stack>
                )}
              </>
            )}
          </Card>
        </SoftBox>
        <Footer />
      </SoftBox>

      <Dialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">{dialogTitle}</DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            {dialogContent}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)} color="primary">
            {t("Non")}
          </Button>
          <Button onClick={handleConfirmAction} color="primary" autoFocus>
            {t("Oui")}
          </Button>
        </DialogActions>
      </Dialog>
    </DashboardLayout>
  );
}

export default OnWayOrders;
